import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';

import { axiosI } from '@/plugins';
import { ApiHandler } from '@/submodules/postman-to-ts/apiHandler';

// Backend contract: himam documentation/ONBOARDING-JOURNEY-SPEC.md §2-3
// (+ KP-63: JOURNEY — a step embedding a checklist/journey that completes
// only once that journey is complete for the user).
export type OnboardingStepType =
  | 'VIDEO'
  | 'CAROUSEL'
  | 'CARDS_SIGN'
  | 'CARDS_ACCEPT'
  | 'QUIZ'
  | 'ASSESSMENT'
  | 'CERTIFICATE'
  | 'JOURNEY';

export type OnboardingStepStatus = 'NOT_STARTED' | 'COMPLETED' | 'SKIPPED';

export interface OnboardingStep {
  key: string;
  type: OnboardingStepType;
  order: number;
  config: Record<string, unknown>;
  status: OnboardingStepStatus;
  isSkippable: boolean;
}

interface OnboardingStateResponse {
  steps: OnboardingStep[];
  nextStepKey: string | null;
  complete: boolean;
}

interface OnboardingStepCompletionResponse {
  nextStepKey: string | null;
  journeyComplete: boolean;
}

/**
 * Onboarding journey state — fetched lazily once per session (see
 * `fetchState`) for authenticated users when the org's `onboarding-journey`
 * config flag is on. Consumed by the router guard (router/index.ts) and the
 * wizard (views/internal-team/onboarding/*).
 */
export const useOnboardingStore = defineStore('onboarding', () => {
  const steps: Ref<OnboardingStep[]> = ref([]);
  const nextStepKey: Ref<string | null> = ref(null);
  const complete: Ref<boolean> = ref(false);
  const loading: Ref<boolean> = ref(false);
  // Whether `fetchState` has resolved at least once this session — the
  // signal `fetchState` uses to avoid refetching on every router navigation.
  const loaded: Ref<boolean> = ref(false);
  const error: Ref<AxiosError | null> = ref(null);
  // Shared by concurrent callers (router guard + store boot can race) so a
  // second caller awaits the first request instead of firing a duplicate.
  let inFlight: Promise<void> | null = null;

  const orderedSteps = computed(() =>
    [...steps.value].sort((a, b) => a.order - b.order)
  );

  const currentStep = computed<OnboardingStep | null>(
    () => steps.value.find((step) => step.key === nextStepKey.value) ?? null
  );

  const currentStepIndex = computed(() =>
    orderedSteps.value.findIndex((step) => step.key === nextStepKey.value)
  );

  const totalSteps = computed(() => steps.value.length);

  const fetchState = async (force = false): Promise<void> => {
    if (loaded.value && !force) return;
    if (inFlight) return inFlight;
    loading.value = true;
    error.value = null;
    inFlight = (async () => {
      try {
        const { path, method } = ApiHandler.onboarding.getState;
        const { data } = await axiosI<OnboardingStateResponse>(path(), {
          method,
        });
        steps.value = data.steps ?? [];
        nextStepKey.value = data.nextStepKey ?? null;
        complete.value = !!data.complete;
        loaded.value = true;
      } catch (fetchError) {
        error.value = fetchError as AxiosError;
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  };

  const applyCompletionResponse = (
    key: string,
    status: OnboardingStepStatus,
    response: OnboardingStepCompletionResponse
  ) => {
    const step = steps.value.find((s) => s.key === key);
    if (step) step.status = status;
    nextStepKey.value = response.nextStepKey ?? null;
    complete.value = !!response.journeyComplete;
  };

  const completeStep = async (
    key: string,
    // Defaults to {} — a caller passing nothing must still POST a JSON
    // body. axios sends no body at all for `data: undefined`, and the
    // backend's Zod DTO rejects an absent body outright (400), which
    // silently stranded learners on every non-VIDEO step.
    payload: Record<string, unknown> = {}
  ): Promise<OnboardingStepCompletionResponse> => {
    const { path, method } = ApiHandler.onboarding.completeStep;
    const { data } = await axiosI<OnboardingStepCompletionResponse>(
      path({ key }),
      { method, data: payload }
    );
    applyCompletionResponse(key, 'COMPLETED', data);
    return data;
  };

  const skipStep = async (
    key: string
  ): Promise<OnboardingStepCompletionResponse> => {
    const { path, method } = ApiHandler.onboarding.skipStep;
    // Same no-body pitfall as completeStep — always send {} explicitly.
    const { data } = await axiosI<OnboardingStepCompletionResponse>(
      path({ key }),
      { method, data: {} }
    );
    applyCompletionResponse(key, 'SKIPPED', data);
    return data;
  };

  // Called on logout so a new session's fetchState isn't skipped by a stale
  // `loaded` flag from the previous user.
  const reset = () => {
    steps.value = [];
    nextStepKey.value = null;
    complete.value = false;
    loaded.value = false;
    error.value = null;
    inFlight = null;
  };

  return {
    steps,
    orderedSteps,
    nextStepKey,
    currentStep,
    currentStepIndex,
    totalSteps,
    complete,
    loading,
    loaded,
    error,
    fetchState,
    completeStep,
    skipStep,
    reset,
  };
});
